import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useFulfillListQuery } from "src/api/fulfillAPI";
import { useAppSelector } from "src/store/actions/action";
import { deleteTypeSearch, setListData, setListDataInit } from "src/store/reducers/fulfillListReduce";
import reuseQueryString from "src/utils/reuseQueryString";
import { getPathValue } from "src/utils/queryStringUtil";
import Body from "src/components/common/Body";
import Box from "src/components/common/Box";
import Button from "src/components/common/Button";
import IconButton from "src/components/common/IconButton";
import SVG from "src/utils/SVG";
import SearchInput from "src/components/common/SearchInput";
import List from "src/components/common/List";
import SearchSortItem from "src/components/filter/SearchSortItem";
import FulFillShopItem from "src/components/notice/FulFillShopItem";
import FulfillFilterItem from "src/components/filter/FulfillFilterItem";
import PaginationCommon from "src/components/common/PaginationCommon";
import BottomModal, { RefProps } from "src/components/common/BottomModal";
import styled from "styled-components";
import Chip from "src/components/common/Chip";
import { useGetFulfilExcelMutation, useGetS3UrlMutation } from "../../api/noticeAPI";

const initFilterData: any = {
  filterList: {
    fulfillFilter: {
      title: "이행 여부",
      data: [
        { type: "이행", checked: false },
        { type: "미이행", checked: false },
      ],
    },
    assessFilter: {
      title: "평가 여부",
      data: [
        { type: "평가 전", checked: false },
        { type: "평가 완료", checked: false },
      ],
    },
    typeFilter: {
      title: "매장 유형",
      data: [
        { type: "직영점", checked: false },
        { type: "대리점", checked: false },
        { type: "백화점", checked: false },
        { type: "면세점", checked: false },
        { type: "아울렛", checked: false },
      ],
    },
  },
  applyFilterList: {},
  fulfillSearchArray: [],
  keywordSearchData: "",
  sortSearchData: "최신순",
  sortSearchIdx: 0,
  reExecuteData: "",
  listTypeData: "",
};

function fulfillList() {
  // 글로벌
  const navigate = useNavigate();
  const navigateState = useLocation();
  const dispatch = useDispatch();
  const noId: any = getPathValue(); // 이행번호
  const fulfillListReduce = useAppSelector((state) => state.fulfillListReduce);
  const storeCurrentPage = useAppSelector((state) => state.fulfillListReduce.currentPageNumber);
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);

  const [reToggle, setReToggle] = useState(fulfillListReduce.reExecuteData === "Y" ? true : false);
  const [listType, setListType] = useState(userInfo.user_type === "S" ? "grid" : fulfillListReduce.listTypeData); // list, grid

  const [api, setApi] = useState({
    apiSkip: true,
    filteredData: "",
  });
  const { apiSkip, filteredData } = api;

  const [queryString, setQueryString] = useState<any>({
    fulfillSearch: fulfillListReduce.fulfillSearchArray, // 이행여부, 평가여부, 매장유형
    contentsSearchString: fulfillListReduce.keywordSearchData, // 검색어
    fulfillSortSearchString: fulfillListReduce.sortSearchData, // sort
    reExecuteSearchString: fulfillListReduce.reExecuteData, // 재이행 여부
    pageSearchNumber: 0, // page
  });

  const [currentPage, setCurrentPage] = useState<number | any>();
  const [totalPages, setTotalPages] = useState();

  // ================================================================================================
  // API 영역
  // ================================================================================================
  const fulfillList = useFulfillListQuery({ notice_idx: noId, search: filteredData }, { skip: apiSkip });

  const [getExcelData] = useGetFulfilExcelMutation();

  // 쿼리스트링 변경에 따른 api 호출
  useEffect(() => {
    if (queryString) {
      const searchFilter = reuseQueryString(queryString);
      setApi({
        ...api,
        filteredData: searchFilter,
        apiSkip: false,
      });
    }
  }, [queryString]);

  // ================================================================================================
  // 기능별 개발 영역
  // ================================================================================================
  // 재이행만 필터
  const searchReExecute = () => {
    if (reToggle) {
      // init : false -> 재이행만 해제시 모두 나와야함
      setQueryString({ ...queryString, reExecuteSearchString: "", pageSearchNumber: 1 });
      dispatch(
        setListData({
          reExecuteData: "",
          currentPageNumber: 1,
        }),
      );
    } else {
      setQueryString({ ...queryString, reExecuteSearchString: "Y", pageSearchNumber: 1 });
      dispatch(
        setListData({
          reExecuteData: "Y",
          currentPageNumber: 1,
        }),
      );
    }
    setCurrentPage(1);
    setReToggle(!reToggle);
  };

  // 페이지네이션
  useEffect(() => {
    setCurrentPage(storeCurrentPage);
    setQueryString({
      ...queryString,
      pageSearchNumber: storeCurrentPage,
      filterList: fulfillListReduce.applyFilterList,
      fulfillSearch: fulfillListReduce.fulfillSearchArray,
    });
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(storeCurrentPage);
    setQueryString({
      ...queryString,
      pageSearchNumber: storeCurrentPage,
      filterList: fulfillListReduce.applyFilterList,
      fulfillSearch: fulfillListReduce.fulfillSearchArray,
    });
  }, [currentPage]);

  useEffect(() => {
    if (fulfillList.isSuccess) {
      setTotalPages(fulfillList.data.data.meta.totalPages);
    }
  }, [fulfillList]);

  const objectChange = (currentPage: number) => {
    setCurrentPage(Number(currentPage));
    dispatch(
      setListData({
        currentPageNumber: currentPage,
      }),
    );
    setQueryString({
      ...queryString,
      pageSearchNumber: currentPage,
    });
  };

  // 키워드 검색
  const [searchKeyword, setSearchKeyword] = useState(fulfillListReduce.keywordSearchData ? fulfillListReduce.keywordSearchData : "");

  const onSearchKeywords = (e: any) => {
    const contents = e.currentTarget.parentElement?.childNodes[0].value;
    setSearchKeyword(contents);
    setCurrentPage(1);
    dispatch(
      setListData({
        keywordSearchData: contents,
        currentPageNumber: 1,
      }),
    );
    setQueryString({
      ...queryString,
      pageSearchNumber: 1,
      contentsSearchString: contents,
    });
  };

  // 키워드 엔터로 검색
  const onKeyPress = (e: any) => {
    if (e.key === "Enter") {
      onSearchKeywords(e);
    }
  };

  // 검색어 삭제
  const deleteSearchContents = () => {
    if (searchKeyword) {
      setCurrentPage(1);
      setSearchKeyword("");
      dispatch(
        setListData({
          keywordSearchData: "",
        }),
      );
      setQueryString({
        ...queryString,
        pageSearchNumber: 1,
        contentsSearchString: "",
      });
    }
  };

  // 필터모달 관련
  const filterModalRef = React.useRef<RefProps>(null);
  const [filterData, setFilterData] = useState<any>(initFilterData);
  const [applyFilterData, setApplyFilterData] = useState<any>(initFilterData);
  const [applyFilterItem, setApplyFilterItem] = useState(fulfillListReduce.fulfillSearchArray);
  const [initVisible, setInitVisible] = useState<boolean>(false);

  useEffect(() => {
    setFilterData(fulfillListReduce.filterList);
    setApplyFilterData(fulfillListReduce.applyFilterList);
    setApplyFilterItem(fulfillListReduce.fulfillSearchArray);
  }, [fulfillListReduce]);

  const openFilterModal = useCallback(() => {
    if (filterModalRef.current) filterModalRef.current.modalOpen();
  }, []);

  const initBtn = () => {
    // fulfillListReduce.filterList만 initState로 초기화
    dispatch(setListDataInit());
  };

  const setInitFunc = (propsSelectedFliterCheck: boolean) => {
    setInitVisible(propsSelectedFliterCheck);
  };

  const confirmBtn = () => {
    setApplyFilterItem(fulfillListReduce.fulfillSearchArray);
    dispatch(
      setListData({
        applyFilterList: fulfillListReduce.filterList,
        fulfillSearchArray: fulfillListReduce.fulfillSearchArray,
        currentPageNumber: 1,
      }),
    );
    setQueryString({
      ...queryString,
      pageSearchNumber: 1,
      fulfillSearch: fulfillListReduce.fulfillSearchArray,
    });
    setCurrentPage(1);
    if (filterModalRef.current) filterModalRef.current.modalClose();
  };

  const closeBtn = () => {
    dispatch(
      setListData({
        filterList: fulfillListReduce.applyFilterList,
        fulfillSearchArray: fulfillListReduce.fulfillSearchArray,
      }),
    );
    if (filterModalRef.current) filterModalRef.current.modalClose();
  };

  // 필터 선택시 박스 스크롤 끝으로 이동
  const filterBoxRef = React.useRef<HTMLDivElement>(null);
  const [filterSelected, setFilterSelected] = useState(false);
  useEffect(() => {
    if (filterBoxRef.current && filterSelected) {
      const scrollX = filterBoxRef.current.scrollLeft;
      const scrollXWidth = filterBoxRef.current.scrollWidth;
      filterBoxRef.current.scrollTo({ top: 0, left: scrollX + scrollXWidth, behavior: "smooth" });
      setFilterSelected(false);
    }
    setFilterSelected(true);
  }, [applyFilterItem]);

  // 필터 칩 삭제
  const deleteSelected = (v: any, i: number) => {
    // store에 typeSearchArray(선택된요소) 지워주기 : 필터 옆 chip 제거
    dispatch(deleteTypeSearch(v));
    const remainChips = fulfillListReduce.fulfillSearchArray?.filter((type) => type !== v);
    const deletedChips = fulfillListReduce.fulfillSearchArray?.filter((type) => type === v).toString();

    setApplyFilterItem(remainChips);

    const getSelectedFulfill = applyFilterData.fulfillFilter.data.map((value: any) =>
      value.type === deletedChips ? { ...value, checked: false } : { ...value },
    );
    const getSelectedAssess = applyFilterData.assessFilter.data.map((value: any) =>
      value.type === deletedChips ? { ...value, checked: false } : { ...value },
    );
    const getSelectedType = applyFilterData.typeFilter.data.map((value: any) => (value.type === deletedChips ? { ...value, checked: false } : { ...value }));

    dispatch(
      setListData({
        filterList: {
          fulfillFilter: {
            title: "이행 여부",
            data: getSelectedFulfill,
          },
          assessFilter: {
            title: "평가 여부",
            data: getSelectedAssess,
          },
          typeFilter: {
            title: "매장 유형",
            data: getSelectedType,
          },
        },
        applyFilterList: {
          fulfillFilter: {
            title: "이행 여부",
            data: getSelectedFulfill,
          },
          assessFilter: {
            title: "평가 여부",
            data: getSelectedAssess,
          },
          typeFilter: {
            title: "매장 유형",
            data: getSelectedType,
          },
        },
      }),
    );

    setQueryString({
      ...queryString,
      pageSearchNumber: 1,
      fulfillSearch: remainChips,
    });
  };

  // 정렬모달 관련
  const [sortIdx, setSortIdx] = useState<number>(fulfillListReduce.sortSearchIdx ? fulfillListReduce.sortSearchIdx : 0);
  const [sortName, setSortName] = useState<string>(fulfillListReduce.sortSearchData ? fulfillListReduce.sortSearchData : "최신순");
  const sortModalRef = React.useRef<RefProps>(null);

  const openSortModal = useCallback(() => {
    if (sortModalRef.current) sortModalRef.current.modalOpen();
  }, []);

  const sortBtn = async (e: any) => {
    setCurrentPage(1);
    const sortName = e.target.textContent;
    setSortName(sortName);
    dispatch(
      setListData({
        sortSearchData: sortName,
        currentPageNumber: 1,
      }),
    );
    setQueryString({
      ...queryString,
      // pageSearchNumber: 1,
      fulfillSortSearchString: sortName,
    });
    if (sortModalRef.current) sortModalRef.current.modalClose();
  };

  const childrenSortIdxFunc = (idx: number) => {
    setSortIdx(idx);
    dispatch(
      setListData({
        sortSearchIdx: idx,
      }),
    );
  };

  const handleClick = async () => {
    try {
      await getExcelData(noId);
    } catch (e) {
      console.log(e);
    }
  };
  // 리스트 뷰 타입 변경
  const changeList = () => {
    setListType("list");
    dispatch(
      setListData({
        listTypeData: "list",
      }),
    );
  };
  const changeGrid = () => {
    setListType("grid");
    dispatch(
      setListData({
        listTypeData: "grid",
      }),
    );
  };

  return (
    <>
      <header>
        <div>
          <Button variant="text" className="btn-back" onClick={() => navigate(-1)}>
            이행목록
          </Button>
        </div>
      </header>

      <Body>
        <Box pd="20px 15px">
          <SearchInput
            type="text"
            id="search-input"
            placeholder="검색어를 입력해주세요."
            defaultValue={searchKeyword}
            onClick={(e) => onSearchKeywords(e)}
            onKeyPress={(e) => onKeyPress(e)}
            deleteSearchContentFunc={deleteSearchContents}
          />
        </Box>

        <FilterBtnSet ref={filterBoxRef}>
          <Button variant="contained" br="round" color="secondary" size="medium" onClick={openFilterModal}>
            <SVG name="filter" width={16} height={16} viewBox="0 0 16 16" />
            필터
          </Button>
          <Button variant="contained" br="round" color="secondary" size="medium" onClick={openSortModal}>
            <SVG name="sort" width={14.637} height={16.348} viewBox="0 0 14.637 16.348" />
            {sortName}
          </Button>
          <Button className="only-new" variant="contained" br="round" color={reToggle ? "active" : "secondary"} size="medium" onClick={searchReExecute}>
            {reToggle ? (
              <SVG name="toggleActive" width={16} height={16} viewBox="0 0 16 16" />
            ) : (
              <SVG name="toggle" width={16} height={16} viewBox="0 0 16 16" />
            )}
            재이행만
          </Button>
          {applyFilterItem &&
            applyFilterItem.map((v: any, i: number) => {
              return (
                <Chip variant="contained" deleted={true} key={i} onDelete={() => deleteSelected(v, i)}>
                  {v}
                </Chip>
              );
            })}
        </FilterBtnSet>

        <TitleBox>
          <div style={{ display: "flex", gap: "1rem", alignItems: "end" }}>
            <div className="num">총 {fulfillList.isSuccess && fulfillList.data.data.meta.totalItems}건</div>
            {userInfo.user_type === "E" && !window.ReactNativeWebView && (
              <IconButton btnName="엑셀 다운로드" className="logout" onClick={handleClick}>
                <SVG name="fileDownload" width={18} height={18} viewBox="0 0 18 18" />
              </IconButton>
            )}
          </div>
          <div className="right-area">
            <ul>
              {userInfo.user_type === "E" && (
                <>
                  <li>
                    <IconButton btnName="리스트형 보기" className="logout" onClick={changeList}>
                      {listType === "list" ? (
                        <SVG name="listViewActive" width={24} height={24} viewBox="0 0 24 24" />
                      ) : (
                        <SVG name="listView" width={24} height={24} viewBox="0 0 24 24" />
                      )}
                    </IconButton>
                  </li>
                  <li>
                    <IconButton btnName="그리드형 보기" className="logout" onClick={changeGrid}>
                      {listType === "grid" ? (
                        <SVG name="gridViewActive" width={24} height={24} viewBox="0 0 24 24" />
                      ) : (
                        <SVG name="gridView" width={24} height={24} viewBox="0 0 24 24" />
                      )}
                    </IconButton>
                  </li>
                </>
              )}
            </ul>
          </div>
        </TitleBox>

        <FulfillShopList className={listType}>
          <FulFillShopItem type={listType} data={fulfillList.isSuccess && fulfillList.data.data} />
        </FulfillShopList>

        {totalPages !== undefined && totalPages > 1 && (
          <PaginationCommon item_count={20} current_page={currentPage} total_page={totalPages} objectChange={objectChange} />
        )}
      </Body>

      <BottomModal
        ref={filterModalRef}
        onClick={() => initBtn()}
        onClick2={() => confirmBtn()}
        onClick3={() => closeBtn()}
        modalTitle="검색 필터"
        headerBtn="초기화"
        footerBtn="적용"
        dimClickFun={closeBtn}
        initVisible={initVisible}
      >
        <List pd="0 15px" itemPd="20px 0">
          <FulfillFilterItem filterList={filterData} setInitFunc={(filter: any) => setInitFunc(filter)} />
        </List>
      </BottomModal>

      <BottomModal modalTitle="정렬" ref={sortModalRef}>
        <List pd="0 15px">
          <SearchSortItem
            sortData={["최신순", "오래된순", "매장명순", "별점높은순", "별점낮은순"]}
            onClick={(e) => sortBtn(e)}
            initIdx={sortIdx}
            parentSortIdxFunc={childrenSortIdxFunc}
          />
        </List>
      </BottomModal>
    </>
  );
}

const FilterBtnSet = styled.div`
  overflow: hidden;
  overflow-x: scroll;
  width: 100%;
  padding: 15px;
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start")}
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
  > * {
    flex: 0 0 auto;
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
  > button {
    > svg {
      position: relative;
      top: -1px;
    }
  }
  .only-new {
    > svg {
      top: 2px;
      left: 2px;
    }
  }
`;

const TitleBox = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin()}
  padding: 12px 15px;
  .num {
    color: #747474;
    font-size: 12px;
  }
  .right-area {
    ul {
      ${({ theme }) => theme.flexBox.flexMixin()}
      > li {
        position: relative;
        margin-left: 12px;
        padding-left: 12px;
        &:before {
          content: "";
          position: absolute;
          top: 50%;
          left: 0;
          display: inline-block;
          width: 1px;
          height: 16px;
          margin-top: -8px;
          background-color: ${({ theme }) => theme.color.lineSecondary};
        }
        &:first-child {
          margin-left: 0;
          padding-left: 0;
          &:before {
            display: none;
          }
        }
      }
    }
  }
  & + .list {
    border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  }
  & + .grid {
    border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  }
`;

const FulfillShopList = styled.ul`
  &.list {
    width: 100%;
    padding: 8px 15px;
  }
  &.grid {
    padding: 24px 1px 0 15px;
    ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start", "flex-start", "wrap")}
    > li {
      width: 50%;
      padding: 0 14px 24px 0;
    }
  }
`;

export default fulfillList;
